import React, { useState } from 'react';
import { ColumnType } from 'antd/lib/table';

import './index.less';

import {
  ELMA365ComponentChangeModelOutput,
  ELMA365ComponentDiff,
  ELMA365ComponentDiffItem,
  ELMA365ComponentDiffPart,
  ELMA365ComponentDiffType,
  ELMA365ToolAction,
  ELMA365ToolModel,
} from '../../../../../../gamma-api';
import IbDebounceInput from '../../../../../components/common/IbDebounceInput';
import IbTable from '../../../../../components/common/IbTable';
import IbTypography from '../../../../../components/common/IbTypography';
import IbTextHighlighter, { MIN_HIGHLIGHTED_SPAN_LENGTH } from '../../../../../components/common/IbTextHighlighter';
import IbIcon from '../../../../../components/common/IbIcon';
import IbCheckbox from '../../../../../components/common/IbCheckbox';
import { ELMA365_COMPONENT_TYPE_ICON_NAMES, ELMA365_TOOL_ACTION_ICON_NAMES } from '../../const';
import { getTranslateFunc } from '../../../../../utils/stringUtil';

import {
  LABEL_CLASS_NAME,
  MAIN_CLASS_NAME,
  SEARCH_CLASS_NAME,
  CHECKBOX_CLASS_NAME,
  TOOLS_CLASS_NAME,
  TOOLS_ITEM_CLASS_NAME,
  DIFFS_CLASS_NAME,
  DIFFS_ITEM_CLASS_NAME,
} from './const';

interface IChangeTableProps {
  changes?: ELMA365ComponentChangeModelOutput[];
  toggledKeys?: { [key: string]: boolean };
  onToggle?: (key: string, selected: boolean) => void;
}

const ChangeTable: React.FC<IChangeTableProps> = ({ changes, toggledKeys, onToggle }) => {
  const [searchText, setSearchText] = useState('');

  const translate = getTranslateFunc('Toolkits: ELMA365: Changes:');

  const isChangeSelected = (change: ELMA365ComponentChangeModelOutput) => {
    const key = change.key.join('.');
    return toggledKeys?.[key] ?? false;
  };

  const onSearchTextChange = (value: string) => setSearchText(value);

  const onChangeToggle = (change: ELMA365ComponentChangeModelOutput) => {
    const key = change.key.join('.');
    onToggle?.(key, !isChangeSelected(change));
  };

  const getComponentType = (change: ELMA365ComponentChangeModelOutput) => {
    if (change.old) {
      return change.old.identity.type;
    }

    if (change.new) {
      return change.new.identity.type;
    }
  };

  const getComponentCode = (change: ELMA365ComponentChangeModelOutput) => {
    if (change.old) {
      return change.old.identity.code;
    }

    if (change.new) {
      return change.new.identity.code;
    }

    return '';
  };

  const getComponentName = (change: ELMA365ComponentChangeModelOutput) => {
    if (change.old) {
      return change.old.identity.name;
    }

    if (change.new) {
      return change.new.identity.name;
    }

    return '';
  };

  const getNamespaceCode = (change: ELMA365ComponentChangeModelOutput) => {
    if (change.old) {
      return change.old.namespace.code;
    }

    if (change.new) {
      return change.new.namespace.code;
    }

    return '';
  };

  const getNamespaceName = (change: ELMA365ComponentChangeModelOutput) => {
    if (change.old) {
      return change.old.namespace.name;
    }

    if (change.new) {
      return change.new.namespace.name;
    }

    return '';
  };

  const formatDiffText = (diff: ELMA365ComponentDiff) => {
    if (diff.item === ELMA365ComponentDiffItem.Application) {
      if (diff.type === ELMA365ComponentDiffType.Created) {
        return translate('Diffs: Application: Created');
      }

      if (diff.type === ELMA365ComponentDiffType.Updated) {
        return translate('Diffs: Application: Updated');
      }

      if (diff.type === ELMA365ComponentDiffType.Deleted) {
        return translate('Diffs: Application: Deleted');
      }
    }

    if (diff.item === ELMA365ComponentDiffItem.Process) {
      if (diff.type === ELMA365ComponentDiffType.Created) {
        return translate('Diffs: Process: Created');
      }

      if (diff.type === ELMA365ComponentDiffType.Updated) {
        return translate('Diffs: Process: Updated');
      }

      if (diff.type === ELMA365ComponentDiffType.Deleted) {
        return translate('Diffs: Process: Deleted');
      }
    }

    if (diff.item === ELMA365ComponentDiffItem.Field) {
      if (diff.type === ELMA365ComponentDiffType.Created) {
        return translate('Diffs: Field: Created', { value: diff.name });
      }

      if (diff.type === ELMA365ComponentDiffType.Deleted) {
        return translate('Diffs: Field: Deleted', { value: diff.name });
      }

      if (diff.type === ELMA365ComponentDiffType.Updated) {
        if (diff.part === ELMA365ComponentDiffPart.Type) {
          return translate('Diffs: Field: Type', { value: diff.name });
        }

        if (diff.part === ELMA365ComponentDiffPart.Name) {
          return translate('Diffs: Field: Name', { value: diff.name });
        }

        if (diff.part === ELMA365ComponentDiffPart.Title) {
          return translate('Diffs: Field: Title', { value: diff.name });
        }

        if (diff.part === ELMA365ComponentDiffPart.Description) {
          return translate('Diffs: Field: Description', { value: diff.name });
        }

        if (diff.part === ELMA365ComponentDiffPart.Required) {
          if (diff.new) {
            return translate('Diffs: Field: Required', { value: diff.name });
          } else {
            return translate('Diffs: Field: Optional', { value: diff.name });
          }
        }
      }
    }

    return `${diff.item} / ${diff.type} / ${diff.part} : ${diff.name} : ${diff.old} - ${diff.new}`;
  };

  const filterChanges = () => {
    if (!changes) {
      return [];
    }

    if (!searchText) {
      return changes;
    }

    const searchTerm = searchText.trim().toLowerCase();
    if (searchTerm.length < MIN_HIGHLIGHTED_SPAN_LENGTH) {
      return changes;
    }

    return changes.filter((change) => {
      const componentCode = getComponentCode(change).toLowerCase();
      if (componentCode.indexOf(searchText) >= 0) {
        return true;
      }

      const componentName = getComponentName(change).toLowerCase();
      if (componentName.indexOf(searchText) >= 0) {
        return true;
      }

      const namespaceCode = getNamespaceCode(change).toLowerCase();
      if (namespaceCode.indexOf(searchText) >= 0) {
        return true;
      }

      const namespaceName = getNamespaceName(change).toLowerCase();
      if (namespaceName.indexOf(searchText) >= 0) {
        return true;
      }

      for (const tool of change.tools) {
        const toolName = tool.name.toLowerCase();
        if (toolName.indexOf(searchText) >= 0) {
          return true;
        }
      }

      return false;
    });
  };

  const renderComponentType = (change: ELMA365ComponentChangeModelOutput) => {
    const type = getComponentType(change) || '';
    return <IbIcon iconName={ELMA365_COMPONENT_TYPE_ICON_NAMES[type]} size={20} />;
  };

  const renderToolAction = (action: ELMA365ToolAction) => {
    return <IbIcon iconName={ELMA365_TOOL_ACTION_ICON_NAMES[action]} size={20} />;
  };

  const renderSearch = () => {
    return (
      <div className={SEARCH_CLASS_NAME}>
        <IbDebounceInput
          allowClear
          placeholder={translate('Search: Placeholder')}
          prefix={<IbIcon iconName="search" />}
          onDebouncedChange={onSearchTextChange}
        />
      </div>
    );
  };

  const renderCheckbox = (change: ELMA365ComponentChangeModelOutput) => {
    const text = getComponentName(change);

    const onCheckboxChange = () => onChangeToggle(change);

    return (
      <div className={CHECKBOX_CLASS_NAME} title={text}>
        <IbCheckbox value={isChangeSelected(change)} onChange={onCheckboxChange} />
        <div className={LABEL_CLASS_NAME}>
          {renderComponentType(change)}
          <IbTypography.Paragraph ellipsis type="secondary">
            <IbTextHighlighter highlightedText={searchText} text={text} />
          </IbTypography.Paragraph>
        </div>
      </div>
    );
  };

  const renderTools = (change: ELMA365ComponentChangeModelOutput) => {
    return (
      <div className={TOOLS_CLASS_NAME}>
        {change.tools.map((tool) => (
          <div key={tool.action} className={TOOLS_ITEM_CLASS_NAME} title={tool.name}>
            {renderToolAction(tool.action)}
            <IbTypography.Paragraph ellipsis type="secondary">
              <IbTextHighlighter highlightedText={searchText} text={tool.name} />
            </IbTypography.Paragraph>
          </div>
        ))}
      </div>
    );
  };

  const renderDiffs = (change: ELMA365ComponentChangeModelOutput) => {
    if (!change.diffs.length) {
      const text = translate('Diffs: Empty');
      return (
        <IbTypography.Paragraph ellipsis light title={text} type="secondary">
          {text}
        </IbTypography.Paragraph>
      );
    }

    return (
      <ul className={DIFFS_CLASS_NAME}>
        {change.diffs.map((diff, index) => {
          const text = formatDiffText(diff);
          return (
            <li
              key={index}
              className={`${DIFFS_ITEM_CLASS_NAME} ${DIFFS_ITEM_CLASS_NAME}_${diff.type.toLowerCase()}`}
              title={text}
            >
              {text}
            </li>
          );
        })}
      </ul>
    );
  };

  const renderTable = () => {
    const columns: ColumnType<ELMA365ComponentChangeModelOutput>[] = [
      {
        title: translate('Table: Name'),
        width: '20%',
        ellipsis: true,
        render: (_, change) => renderCheckbox(change),
      },
      {
        title: translate('Table: Code'),
        width: '20%',
        ellipsis: true,
        render: (_, change) => {
          const text = `${getNamespaceCode(change)} / ${getComponentCode(change)}`;
          return (
            <IbTypography.Paragraph ellipsis title={text} type="secondary">
              <IbTextHighlighter highlightedText={searchText} text={text} />
            </IbTypography.Paragraph>
          );
        },
      },
      {
        title: translate('Table: Namespace'),
        width: '15%',
        ellipsis: true,
        render: (_, change) => {
          const text = getNamespaceName(change);
          return (
            <IbTypography.Paragraph ellipsis title={text} type="secondary">
              <IbTextHighlighter highlightedText={searchText} text={text} />
            </IbTypography.Paragraph>
          );
        },
      },
      {
        title: translate('Table: Tools'),
        width: '20%',
        ellipsis: true,
        render: (_, change) => renderTools(change),
      },
      {
        title: translate('Table: Diffs'),
        width: '25%',
        ellipsis: true,
        render: (_, change) => renderDiffs(change),
      },
    ];

    return (
      <IbTable
        columns={columns}
        dataSource={filterChanges()}
        emptyText={<IbTypography.Paragraph disabled>{translate('Nothing found')}</IbTypography.Paragraph>}
        rowKey={(tool: ELMA365ToolModel) => tool.tool_id}
        size="middle"
        onRowClick={onChangeToggle}
      />
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      {renderSearch()}
      {renderTable()}
    </div>
  );
};

export default ChangeTable;
